'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function WhatsIncludedSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const included = [
    {
      icon: 'ri-flashlight-line',
      title: '1x Premium Pistol Lighter',
      subtitle: 'Tactical Aluminum',
    },
    {
      icon: 'ri-usb-line',
      title: '1x USB Charging Cable',
      subtitle: 'Type-C',
    },
    {
      icon: 'ri-book-open-line',
      title: '1x User Manual',
      subtitle: 'Complete Instructions',
    },
    {
      icon: 'ri-t-shirt-line',
      title: '1x Microfiber Cleaning Cloth',
      subtitle: 'Premium Quality',
    },
    {
      icon: 'ri-gift-line',
      title: '1x Gift Box',
      subtitle: 'Perfect for presents',
    },
  ];

  const details = [
    'Aircraft-grade aluminum construction',
    'Precision-engineered ignition system',
    'Weatherproof sealing',
    '500+ charge cycle battery life',
    'Compatible with all cigarette sizes',
  ];

  return (
    <section
      ref={ref}
      className="py-12 sm:py-16 md:py-24 lg:py-32 bg-white"
    >
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black mb-2 sm:mb-4">
            Premium Package Included
          </h2>
          <p className="text-black/60 text-xs sm:text-sm md:text-base lg:text-lg">
            Everything you need to get started
          </p>
        </motion.div>

        {/* What You Get */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12 md:mb-16">
          {included.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-gray-50 rounded-lg p-4 sm:p-6 text-center"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-black rounded-full flex items-center justify-center">
                <i className={`${item.icon} text-xl sm:text-2xl md:text-3xl text-white`} />
              </div>
              <h3 className="font-serif text-sm sm:text-base md:text-lg text-black mb-1">
                {item.title}
              </h3>
              <p className="text-black/60 text-xs sm:text-sm">
                {item.subtitle}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Quality Details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="bg-black rounded-lg p-6 sm:p-8 md:p-10"
        >
          <h3 className="font-serif text-xl sm:text-2xl md:text-3xl text-white mb-4 sm:mb-6 text-center">
            Quality Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {details.map((detail, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                className="flex items-center gap-3"
              >
                <i className="ri-checkbox-circle-fill text-lg sm:text-xl text-white flex-shrink-0" />
                <span className="text-white/90 text-xs sm:text-sm md:text-base">
                  {detail}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
